import { MappedTheme } from './_mapped_theme';
import type { CssVariable, CssVariableGetter } from './_common';

type CssVariableKeysOf<T extends typeof MappedTheme> = {
	[P in keyof T]: string;
};

function asCssVariableGetter<T extends MappedTheme>(value: T): CssVariableGetter<CssVariable<T>> {
	// TODO: REMOVE THIS ONCE NOT INJECTED ANY MORE
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	return `var(--visiba-global-${value});`;
}

export const theme: ImplementRecordWithKnownKeys<keyof typeof MappedTheme, CssVariableKeysOf<typeof MappedTheme>> = {
	get primary() {
		return asCssVariableGetter(MappedTheme.primary);
	},
	get accentColor() {
		return asCssVariableGetter(MappedTheme.accentColor);
	},
	get background1a() {
		return asCssVariableGetter(MappedTheme.background1a);
	},
	get background1b() {
		return asCssVariableGetter(MappedTheme.background1b);
	},
	get background2() {
		return asCssVariableGetter(MappedTheme.background2);
	},
	get baseButtonClicked() {
		return asCssVariableGetter(MappedTheme.baseButtonClicked);
	},
	get baseButtonDefault() {
		return asCssVariableGetter(MappedTheme.baseButtonDefault);
	},
	get baseButtonDisabled() {
		return asCssVariableGetter(MappedTheme.baseButtonDisabled);
	},
	get baseButtonDisabledText() {
		return asCssVariableGetter(MappedTheme.baseButtonDisabledText);
	},
	get baseButtonHover() {
		return asCssVariableGetter(MappedTheme.baseButtonHover);
	},
	get baseButtonText() {
		return asCssVariableGetter(MappedTheme.baseButtonText);
	},
	get bottomNavigationBarActiveItem() {
		return asCssVariableGetter(MappedTheme.bottomNavigationBarActiveItem);
	},
	get bottomNavigationBarBackground() {
		return asCssVariableGetter(MappedTheme.bottomNavigationBarBackground);
	},
	get bottomNavigationBarInactiveItem() {
		return asCssVariableGetter(MappedTheme.bottomNavigationBarInactiveItem);
	},
	get chatBubble1() {
		return asCssVariableGetter(MappedTheme.chatBubble1);
	},
	get chatBubble2() {
		return asCssVariableGetter(MappedTheme.chatBubble2);
	},
	get chatText1() {
		return asCssVariableGetter(MappedTheme.chatText1);
	},
	get chatText2() {
		return asCssVariableGetter(MappedTheme.chatText2);
	},
	get disabledButton() {
		return asCssVariableGetter(MappedTheme.disabledButton);
	},
	get disabledButtonText() {
		return asCssVariableGetter(MappedTheme.disabledButtonText);
	},
	get hoveredElement() {
		return asCssVariableGetter(MappedTheme.hoveredElement);
	},
	get hyperLinkClicked() {
		return asCssVariableGetter(MappedTheme.hyperLinkClicked);
	},
	get hyperLinkDefault() {
		return asCssVariableGetter(MappedTheme.hyperLinkDefault);
	},
	get hyperLinkHover() {
		return asCssVariableGetter(MappedTheme.hyperLinkHover);
	},
	get iconButtonIcon() {
		return asCssVariableGetter(MappedTheme.iconButtonIcon);
	},
	get iconButtonSecondaryText() {
		return asCssVariableGetter(MappedTheme.iconButtonSecondaryText);
	},
	get imageOverlay() {
		return asCssVariableGetter(MappedTheme.imageOverlay);
	},
	get imageText() {
		return asCssVariableGetter(MappedTheme.imageText);
	},
	get inputFieldBackground() {
		return asCssVariableGetter(MappedTheme.inputFieldBackground);
	},
	get inputFieldBorder() {
		return asCssVariableGetter(MappedTheme.inputFieldBorder);
	},
	get inputFieldIcon() {
		return asCssVariableGetter(MappedTheme.inputFieldIcon);
	},
	get inputFieldPlaceholderText() {
		return asCssVariableGetter(MappedTheme.inputFieldPlaceholderText);
	},
	get inputFieldText() {
		return asCssVariableGetter(MappedTheme.inputFieldText);
	},
	get primaryButtonClicked() {
		return asCssVariableGetter(MappedTheme.primaryButtonClicked);
	},
	get primaryButtonDefault() {
		return asCssVariableGetter(MappedTheme.primaryButtonDefault);
	},
	get primaryButtonHover() {
		return asCssVariableGetter(MappedTheme.primaryButtonHover);
	},
	get primaryButtonText() {
		return asCssVariableGetter(MappedTheme.primaryButtonText);
	},
	get secondaryButtonBorder() {
		return asCssVariableGetter(MappedTheme.secondaryButtonBorder);
	},
	get secondaryButtonClicked() {
		return asCssVariableGetter(MappedTheme.secondaryButtonClicked);
	},
	get secondaryButtonDefault() {
		return asCssVariableGetter(MappedTheme.secondaryButtonDefault);
	},
	get secondaryButtonHover() {
		return asCssVariableGetter(MappedTheme.secondaryButtonHover);
	},
	get secondaryButtonText() {
		return asCssVariableGetter(MappedTheme.secondaryButtonText);
	},
	get secondaryTextInThemedBackground() {
		return asCssVariableGetter(MappedTheme.secondaryTextInThemedBackground);
	},
	get secondaryTextOnWhiteBackground() {
		return asCssVariableGetter(MappedTheme.secondaryTextOnWhiteBackground);
	},
	get themedTextOnWhiteBackground() {
		return asCssVariableGetter(MappedTheme.themedTextOnWhiteBackground);
	},
	get selectedElement() {
		return asCssVariableGetter(MappedTheme.selectedElement);
	},
	get systemColorAttention() {
		return asCssVariableGetter(MappedTheme.systemColorAttention);
	},
	get systemColorError() {
		return asCssVariableGetter(MappedTheme.systemColorError);
	},
	get systemColorFocus() {
		return asCssVariableGetter(MappedTheme.systemColorFocus);
	},
	get systemColorSuccess() {
		return asCssVariableGetter(MappedTheme.systemColorSuccess);
	},
	get textButton() {
		return asCssVariableGetter(MappedTheme.textButton);
	},
	get textOnBackground2() {
		return asCssVariableGetter(MappedTheme.textOnBackground2);
	},
	get textOnThemedBackground() {
		return asCssVariableGetter(MappedTheme.textOnThemedBackground);
	},
	get textOnWhiteBackground() {
		return asCssVariableGetter(MappedTheme.textOnWhiteBackground);
	},
	get topNavigationBarBackground() {
		return asCssVariableGetter(MappedTheme.topNavigationBarBackground);
	},
	get topNavigationBarText() {
		return asCssVariableGetter(MappedTheme.topNavigationBarText);
	},
	get uiCardBackground() {
		return asCssVariableGetter(MappedTheme.uiCardBackground);
	},
	get uiCardSecondaryText() {
		return asCssVariableGetter(MappedTheme.uiCardSecondaryText);
	},
	get uiCardText() {
		return asCssVariableGetter(MappedTheme.uiCardText);
	},
	get uiElements() {
		return asCssVariableGetter(MappedTheme.uiElements);
	},
	get webMainNavigationBarActiveItem() {
		return asCssVariableGetter(MappedTheme.webMainNavigationBarActiveItem);
	},
	get webMainNavigationBarBackground() {
		return asCssVariableGetter(MappedTheme.webMainNavigationBarBackground);
	},
	get webMainNavigationBarHoverIndicator() {
		return asCssVariableGetter(MappedTheme.webMainNavigationBarHoverIndicator);
	},
	get webMainNavigationBarInactiveItem() {
		return asCssVariableGetter(MappedTheme.webMainNavigationBarInactiveItem);
	},
};
